import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { collectAgencyMatchEntries } from 'helpers/agencyManagement/agency-tree-search';
import {
  ACTIVE_FIND_RING_CLASSES,
  AGENCY_TREE_TITLE_DATA_ATTR
} from './agencyTreeFindDom';

const EMPTY_ROOTS = [];

// lets the ancestor sections finish expanding before we scroll to the match
const SCROLL_AFTER_FIND_MS = 280;

function escapeCssAttrValue(value) {
  const str = String(value);
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(str);
  }
  return str.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function scrollElementToVerticalCenter(panel, element, behavior = 'smooth') {
  if (!panel || !element || !panel.contains(element)) return false;

  const panelRect = panel.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  const deltaTop = elementRect.top - panelRect.top;
  const targetScrollTop =
    panel.scrollTop +
    deltaTop -
    panel.clientHeight / 2 +
    elementRect.height / 2;

  const maxScroll = Math.max(0, panel.scrollHeight - panel.clientHeight);

  panel.scrollTo({
    top: Math.max(0, Math.min(targetScrollTop, maxScroll)),
    behavior
  });
  return true;
}

// the sections are uncontrolled checkboxes, so expanding is a direct DOM write
function expandAncestors(expandCheckboxIds) {
  expandCheckboxIds.forEach(checkboxId => {
    const input = document.getElementById(checkboxId);
    if (input && input.tagName === 'INPUT') {
      input.checked = true;
    }
  });
}

function findRowNode(scope, agencyId) {
  return scope.querySelector(
    `[data-agency-id="${escapeCssAttrValue(agencyId)}"]`
  );
}

function findScrollTarget(scope, agencyId) {
  const escaped = escapeCssAttrValue(agencyId);
  const titleNode = scope.querySelector(
    `[${AGENCY_TREE_TITLE_DATA_ATTR}="${escaped}"]`
  );
  return titleNode ?? findRowNode(scope, agencyId);
}

/**
 * Cmd+F-style find over an agencies tree: tracks the query, walks the matches
 * and, for the active one, expands its ancestors, scrolls it into view and
 * rings it. The tree itself is never re-rendered — highlighting and expanding
 * are imperative DOM writes, which keeps typing responsive on large downlines.
 * @param roots - the agencies tree, as stored in agencyManagement.agenciesTree
 * @param nodeIdPrefix - the prefix the tree passes to makeAgencyNodeId
 * @param scrollContainerRef - optional ref to the scrolling panel wrapping the
 *   tree; without it the match is scrolled into view by the browser instead
 */
export default function useAgencyTreeFind({
  roots,
  nodeIdPrefix,
  scrollContainerRef
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [matchIndex, setMatchIndex] = useState(0);

  // Deferred so each keystroke repaints only the input at high priority; the
  // match recompute happens in a low-priority render without blocking typing.
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const searchTrimmed = deferredSearchQuery.trim();

  const matchEntries = useMemo(
    () =>
      collectAgencyMatchEntries(
        roots ?? EMPTY_ROOTS,
        deferredSearchQuery,
        nodeIdPrefix
      ),
    [roots, deferredSearchQuery, nodeIdPrefix]
  );

  const matchCount = matchEntries.length;

  useEffect(() => {
    setMatchIndex(0);
  }, [deferredSearchQuery]);

  useEffect(() => {
    setMatchIndex(i => {
      if (matchCount === 0) return 0;
      return Math.min(i, matchCount - 1);
    });
  }, [matchCount]);

  let activeMatchAgencyId = null;
  if (searchTrimmed && matchCount > 0) {
    activeMatchAgencyId = matchEntries[matchIndex]?.agencyId ?? null;
  }

  useEffect(() => {
    if (!searchTrimmed) return undefined;
    const panel = scrollContainerRef?.current ?? null;
    let timerId;

    if (matchCount === 0) {
      if (!panel) return undefined;
      timerId = window.setTimeout(() => {
        panel.scrollTo({ top: 0, behavior: 'smooth' });
      }, SCROLL_AFTER_FIND_MS);
      return () => window.clearTimeout(timerId);
    }

    const entry = matchEntries[matchIndex];
    if (!entry) return undefined;

    expandAncestors(entry.expandCheckboxIds);

    timerId = window.setTimeout(() => {
      const scope = panel ?? document;
      const target = findScrollTarget(scope, entry.agencyId);
      if (!target) return;
      const scrolled = scrollElementToVerticalCenter(panel, target, 'smooth');
      if (!scrolled) {
        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }, SCROLL_AFTER_FIND_MS);

    return () => window.clearTimeout(timerId);
  }, [searchTrimmed, matchIndex, matchCount, matchEntries, scrollContainerRef]);

  useEffect(() => {
    if (activeMatchAgencyId == null) return undefined;
    const scope = scrollContainerRef?.current ?? document;
    const row = findRowNode(scope, activeMatchAgencyId);
    if (!row) return undefined;
    // On expanded sections the <li> spans the whole subtree, so ring its
    // header row (the direct <label>) instead; leaf rows ring the row itself.
    const target = row.querySelector(':scope > label') ?? row;
    target.classList.add(...ACTIVE_FIND_RING_CLASSES);
    return () => target.classList.remove(...ACTIVE_FIND_RING_CLASSES);
  }, [activeMatchAgencyId, scrollContainerRef]);

  function goPrevMatch() {
    if (matchCount === 0) return;
    setMatchIndex(i => (i - 1 + matchCount) % matchCount);
  }

  function goNextMatch() {
    if (matchCount === 0) return;
    setMatchIndex(i => (i + 1) % matchCount);
  }

  function handleSearchKeyDown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      goNextMatch();
      return;
    }
    if (matchCount === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      goNextMatch();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      goPrevMatch();
    }
  }

  return {
    searchQuery,
    setSearchQuery,
    searchTrimmed,
    matchIndex,
    matchCount,
    goPrevMatch,
    goNextMatch,
    handleSearchKeyDown
  };
}
